import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./PromoCode.scss";

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import TooltipText from "@mui/material/Tooltip";

import EditIcon from "@mui/icons-material/Edit";

import Toaster from "../../component/Toaster";
import DataGrid from "../../component/DataGrid";
import ActionButton from "../../component/ActionButton";

import RightDrawerLarge from "../Drawer/RightDrawerLarge";
import CreatePromoCode from "./CreatePromoCode";

import { promoCodeAction } from "../../store/promoCode/promoCodeSlice";
import { useAppDispatch } from "../../store/hooks";
import { Access, CommonUtil } from "../../util/Utils";
import { ROLE } from "../../routes/roles";
import GlobalLoader from "../../component/GlobalLoader";
import React from "react";

const PromoCodeList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const ref = useRef(true);
  const [spinner, setSpinner] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [promoCodeSingleData, setPromoCodeSingleData] = useState({});
  const [promoCodes, setPromoCodes] = useState<any>([]);
  const [loginType, setLoginType] = useState<any>(null);
  const [accessRights, setAccessRights] = useState<Access>({
    create: false,
    update: false,
    view: false,
    delete: false,
  });

  const [rowId, setRowId] = useState<any>("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const onCloseDeleteDialog = () => setDeleteDialogOpen(false);

  const getPromoCodeById = (selectedId: any, userAction: string) => {
    setSpinner(true);
    dispatch(promoCodeAction({ apiAction: "dataById", selectedId }))
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          setPromoCodeSingleData(result?.payload?.data.data);
          if (userAction === "edit") {
            setEditDrawerOpen(!editDrawerOpen);
          }
        } else {
          Toaster.error(undefined, result.payload);
        }
      })
      .catch((err: any) => {
        // This console for debug the app if any error occurred in DEV
        console.log(err);
      })
      .finally(() => {
        setSpinner(false);
      });
  };

  const handleDeleteModal = (row: any) => {
    setRowId(row.id);
    setDeleteDialogOpen(true);
  };

  const removePromoCode = (promoCodeId: number) => {
    setSpinner(true);
    dispatch(promoCodeAction({ id: promoCodeId, apiAction: "delete" }))
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          Toaster.success(undefined, result.payload.data.message);
        } else {
          Toaster.error(undefined, result.payload);
        }
      })
      .catch((err: any) => {
        Toaster.error(undefined, err);
      })
      .finally(() => {
        getPromoCodeList();
        setDeleteDialogOpen(false);
        setSpinner(false);
      });
  };

  const deletePromoCode = () => {
    onCloseDeleteDialog();
    removePromoCode(rowId);
  };

  const handleDrawerOpen = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleEditDrawerOpen = (row: any) => {
    getPromoCodeById(row.id, "edit");
  };

  const handleEditDrawerClose = () => {
    setEditDrawerOpen(false);
  };

  const columns = [
    {
      name: "ID",
      selector: (row: any) => row.id,
      sortable: true,
      omit: true,
    },
    {
      name: `${t("promoCode.promoCode")}`,
      selector: (row: any) => row.promoCode,
      Cell: (row: any) => <a href={row.promoCode}> {row.promoCode} </a>,
      sortable: "true",
      width: "14%",
    },
    {
      name: `${t("promoCodeDescription")}`,
      selector: (row: any) => row.promoCodeDescription,
      sortable: "true",
      width: "14%",
    },
    {
      name: `${t("active")}`,
      selector: (row: any) => row.active,
      sortable: "true",
      width: "14%",
      center: true,
    },
    {
      name: `${t("effectiveDateRange")}`,
      selector: (row: any) => row.effectiveFromDate,
      cell: (row: any) => (
        <>
          {row.effectiveFromDate} - {row.effectiveToDate}
        </>
      ),
      sortable: "true",
      width: "14%",
      center: true,
    },
    {
      name: `${t("courseName")}`,
      selector: (row: any) => row.courseName,
      sortable: "true",
      width: "14%",
    },
    {
      name: `${t("discountAmt")}`,
      selector: (row: any) => row.discount?.toFixed(2),
      sortable: "true",
      width: "14%",
    },
    {
      name: `${t("action")}`,
      cell: (row: any) => (
        <Box className="grid-action">
          {(loginType === ROLE.HO_ADMIN || accessRights?.update === "Yes") && (
            <TooltipText title={t("edit")} placement="left">
              <button className="edit">
                <EditIcon onClick={() => handleEditDrawerOpen(row)} />
              </button>
            </TooltipText>
          )}
          {/* {(loginType === ROLE.HO_ADMIN || accessRights?.delete === "Yes") && (
            <TooltipText title={t("delete")} placement="left">
              <button className="delete">
                <DeleteIcon onClick={() => handleDeleteModal(row)} />
              </button>
            </TooltipText>
          )} */}
        </Box>
      ),
      center: true,
    },
  ];

  const getPromoCodeList = () => {
    setSpinner(true);
    dispatch(promoCodeAction({ apiAction: "list" }))
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          setPromoCodes(result?.payload?.data.data);
        } else {
          Toaster.error(undefined, result.payload);
        }
      })
      .catch((err: any) => {
        // This console for debug the app if any error occurred in DEV
        console.log(err);
      })
      .finally(() => {
        setSpinner(false);
      });
  };

  // UseEffects
  React.useEffect(() => {
    if (ref.current) {
      ref.current = false;
      getPromoCodeList();
    }
  }, []);

  useEffect(() => {
    setLoginType(CommonUtil.loginTypeHandler());
    const access = CommonUtil.accessCheckHandler("MASTER", "PROMO_CODE");
    setAccessRights({
      ...accessRights,
      create: access?.create,
      update: access?.update,
      view: access?.view,
      delete: access?.delete,
    });
  }, [drawerOpen, editDrawerOpen]);

  const onDeleteSuccess = () => {
    getPromoCodeList();
  };

  return (
    <Box className="module-container">
      <Box display="flex" justifyContent="space-between">
        <Box>
          <label className="sub-module-header popins">{"Promo Code"} </label>
        </Box>
        <Box>
          <ActionButton
            label={t("createPromoCode")}
            actionButtonStyle="dark"
            onClick={handleDrawerOpen}
          />
        </Box>
      </Box>

      <Box className="padding-up-5">
        <DataGrid
          columns={columns}
          data={promoCodes}
          action={promoCodeAction}
          searchParam={[
            "promoCode",
            "promoCodeDescription",
            "active",
            "courseName",
            "discount",
            "effectiveFromDate",
          ]}
          onDeleteSuccess={onDeleteSuccess}
          selectableRowsHighlight={true}
          highlightOnHover={true}
          canMultipleDelete={false}
          canExport={false}
          selectableRows={false}
        />
      </Box>

      {/* drawer */}
      <RightDrawerLarge
        open={drawerOpen}
        onClose={handleDrawerClose}
        title={t("createPromoCode")}
        content={
          <CreatePromoCode
            handleClose={handleDrawerClose}
            getPromoCodeList={getPromoCodeList}
          />
        }
      />

      <RightDrawerLarge
        open={editDrawerOpen}
        onClose={handleEditDrawerClose}
        title={t("editPromoCode")}
        content={
          // <EditPromocode
          <CreatePromoCode
            userAction="edit"
            promoCodeSingleData={promoCodeSingleData}
            handleClose={handleEditDrawerClose}
            getPromoCodeList={getPromoCodeList}
          />
        }
      />

      {/* delete modal */}
      <Dialog
        open={deleteDialogOpen}
        onClose={onCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Delete"}</DialogTitle>

        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t("areyouSureYouWantToDelete")}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <ActionButton
            color="green"
            onClick={onCloseDeleteDialog}
            label={t("cancel")}
          ></ActionButton>

          <ActionButton
            color="red"
            onClick={() => deletePromoCode()}
            autoFocus
            style={{ color: "#F27373" }}
            label={t("delete")}
          ></ActionButton>
        </DialogActions>
      </Dialog>
      <GlobalLoader loading={spinner} />
    </Box>
  );
};

export default PromoCodeList;
